import React, { useState } from 'react';
import { AdjudicationDialog } from '../components/claim/AdjudicationDialog';
import { CorrectVoidDialog } from '../components/claim/CorrectVoidDialog';
import { GenerateTestClaimsDialog } from '../components/claim/GenerateTestClaimsDialog';
import { RecentOdmSubmissions } from '../components/claim/RecentOdmSubmissions';
import { SubmitToOdmDialog } from '../components/claim/SubmitToOdmDialog';
import ClaimsFiltersRail from './claims-page/ClaimsFiltersRail';
import ClaimsHeader from './claims-page/ClaimsHeader';
import ClaimsTable from './claims-page/ClaimsTable';
import ClaimsTabs from './claims-page/ClaimsTabs';
import CopyClaimDialog from './claims-page/CopyClaimDialog';
import TriageDialog from './claims-page/TriageDialog';
import { useClaims } from './claims-page/useClaims';

/**
 * Claims page — thin orchestrator. State + handlers live in `useClaims`,
 * presentational pieces are in `claims-page/`.
 *
 * P34 (Feb 27, 2026): Rhinobill-style 2-column layout — left filter rail
 * (with tabs above), table on the right. Print + Download CSV in header.
 */
const ClaimsPage = () => {
  const c = useClaims();
  const [genTestOpen, setGenTestOpen] = useState(false);

  // Tab counts driven from the already-loaded `claims` array, so they
  // reflect what's on screen without an extra round-trip per tab.
  const tabCounts = {
    recent: c.claims.length,
    drafts: c.claims.filter((cl) => cl.status === 'Draft').length,
    issues: c.claims.filter(
      (cl) => !['Paid', 'Draft', 'Voided'].includes(cl.status)
    ).length,
  };

  const handlePrint = () => {
    // Add a print-only stylesheet hint so the table dominates the page.
    if (typeof window !== 'undefined') window.print();
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="claims-page">
      <ClaimsHeader
        totalCount={c.claims.length}
        draftCount={tabCounts.drafts}
        selectedCount={c.selectedClaims.length}
        onBatchSubmit={c.handleBatchSubmit}
        onOpenOdmDialog={() => c.setOdmDialogOpen(true)}
        onOpenGenerateTestClaims={() => setGenTestOpen(true)}
        onPrint={handlePrint}
        onExportCsv={c.exportCsv}
      />

      {/* Recent ODM Submissions — auto-refreshes every 60s */}
      <RecentOdmSubmissions refreshKey={c.claims.length} />

      <ClaimsTabs activeTab={c.activeTab} onChange={c.setActiveTab} counts={tabCounts} />

      <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-6">
        <ClaimsFiltersRail
          search={c.search}
          setSearch={c.setSearch}
          filterStatus={c.filterStatus}
          setFilterStatus={c.setFilterStatus}
          filterPayer={c.filterPayer}
          setFilterPayer={c.setFilterPayer}
          filterProcedure={c.filterProcedure}
          setFilterProcedure={c.setFilterProcedure}
          filterServiceFrom={c.filterServiceFrom}
          setFilterServiceFrom={c.setFilterServiceFrom}
          filterServiceTo={c.filterServiceTo}
          setFilterServiceTo={c.setFilterServiceTo}
          filterClaimNumber={c.filterClaimNumber}
          setFilterClaimNumber={c.setFilterClaimNumber}
          filterIcn={c.filterIcn}
          setFilterIcn={c.setFilterIcn}
          filterCreatedFrom={c.filterCreatedFrom}
          setFilterCreatedFrom={c.setFilterCreatedFrom}
          filterCreatedTo={c.filterCreatedTo}
          setFilterCreatedTo={c.setFilterCreatedTo}
          filterEdi277Category={c.filterEdi277Category}
          setFilterEdi277Category={c.setFilterEdi277Category}
          filterEdi277Code={c.filterEdi277Code}
          setFilterEdi277Code={c.setFilterEdi277Code}
          filterEdi277Entity={c.filterEdi277Entity}
          setFilterEdi277Entity={c.setFilterEdi277Entity}
          filterOptions={c.filterOptions}
          activeFilterCount={c.activeFilterCount}
          onClear={c.clearFilters}
          savedViews={c.savedViews}
          onSaveCurrentView={c.saveCurrentView}
          onApplySavedView={c.applySavedView}
          onDeleteSavedView={c.deleteSavedView}
        />

        <ClaimsTable
          loading={c.loading}
          filteredClaims={c.filteredClaims}
          selectableCount={c.selectableClaims.length}
          selectedClaims={c.selectedClaims}
          onSelectAll={c.handleSelectAll}
          onSelect={c.handleSelectClaim}
          onCopy={c.openCopyModal}
          onSubmit={c.handleSubmitSingle}
          onDelete={c.handleDelete}
          onOpenAdjudication={c.setAdjudicationClaim}
          onOpenTriage={c.openTriage}
          onOpenCorrect={c.openCorrect}
          onOpenVoid={c.openVoid}
          onMarkFixed={c.handleMarkFixed}
        />
      </div>

      <CopyClaimDialog
        open={c.copyModalOpen}
        onOpenChange={c.setCopyModalOpen}
        newServiceDate={c.newServiceDate}
        setNewServiceDate={c.setNewServiceDate}
        copying={c.copying}
        onCopy={c.handleCopyClaim}
      />

      <SubmitToOdmDialog
        open={c.odmDialogOpen}
        claimIds={c.selectedClaims}
        onClose={() => c.setOdmDialogOpen(false)}
        onSubmitted={() => {
          c.setSelectedClaims([]);
          c.setOdmDialogOpen(false);
          c.fetchClaims();
        }}
      />

      <TriageDialog
        triageClaim={c.triageClaim}
        triageNotes={c.triageNotes}
        setTriageNotes={c.setTriageNotes}
        triaging={c.triaging}
        onClose={() => c.setTriageClaim(null)}
        onConfirm={c.handleTriage}
      />

      <AdjudicationDialog
        claimId={c.adjudicationClaim?.id}
        claimNumber={c.adjudicationClaim?.claim_number}
        open={!!c.adjudicationClaim}
        onClose={() => c.setAdjudicationClaim(null)}
      />

      <CorrectVoidDialog
        open={!!c.correctVoid}
        mode={c.correctVoid?.mode}
        claim={c.correctVoid?.claim || null}
        onClose={c.closeCorrectVoid}
        onSuccess={c.handleCorrectVoidSuccess}
      />

      <GenerateTestClaimsDialog
        open={genTestOpen}
        onClose={() => setGenTestOpen(false)}
        onGenerated={() => c.fetchClaims()}
      />
    </div>
  );
};

export default ClaimsPage;
